import { Button, Typography, Form, Table, Input } from "antd";
import { useEffect } from "react";
import styles from "./timeline-form.module.css";
import Text from "../../context/language-context";
import useDrawer from "../../hooks/useDrawer";
import { eResultCode, eTimeline } from "../../utils/enum";
import { addEditTimeline, getTimeline } from "../../utils/api.constant";
import useFetch from "../../hooks/useFetch";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import { FaCheck } from "react-icons/fa";
import useToast from "../../hooks/useToast";
import { getNumberInLanguage } from "../../utils/helperFunctions";

const TimelineForm = () => {
  const { onCloseDrawer } = useDrawer();
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const stages = [
    {
      stage: `${Text({ tid: "STAGE", def: "Stage" })} ${getNumberInLanguage(1)}`,
      key: eTimeline.PT1_SUBMISSION,
      status: Text({ tid: "FORM_SUBMISSION", def: "Form Submission" }),
    },
    {
      stage: `${Text({ tid: "STAGE", def: "Stage" })} ${getNumberInLanguage(1)}`,
      key: eTimeline.PT1_APPROVAL,
      status: Text({ tid: "FORM_APPROVAL", def: "Form Approval" }),
    },
    {
      stage: `${Text({ tid: "STAGE", def: "Stage" })} ${getNumberInLanguage(2)}`,
      key: eTimeline.PT2_SUBMISSION,
      status: Text({ tid: "FORM_SUBMISSION", def: "Form Submission" }),
    },
    {
      stage: `${Text({ tid: "STAGE", def: "Stage" })} ${getNumberInLanguage(2)}`,
      key: eTimeline.PT2_APPROVAL,
      status: Text({ tid: "FORM_APPROVAL", def: "Form Approval" }),
    },
    {
      key: eTimeline.INTERVIEW_STAGE,
      stage: Text({ tid: "INTERVIEW_STAGE", def: "Interview Stage" }),
      status: Text({ tid: "INTERVIEW_PROCESS", def: "Interview Process" }),
    },
    {
      key: eTimeline.SELECTION_DATE,
      stage: Text({ tid: "FORM_SELECTION", def: "Form Selection" }),
    },
    {
      key: eTimeline.AWARD_DATE,
      stage: Text({ tid: "AWARD_DATE", def: "Award Date" }),
    },
  ];

  const columns = [
    {
      title: Text({ tid: "STAGE", def: "Stage" }),
      dataIndex: "stage",
      key: "stage",
      render: (text: string, record: any) => (
        <div>
          <strong>{text}</strong>
          {record.status && <div>{record.status}</div>}
        </div>
      ),
    },
    {
      title: Text({ tid: "START_DATE", def: "Start Date" }),
      dataIndex: "startDate",
      key: "startDate",
      render: (text: string, record: any) => (
        <Form.Item
          name={["data", record.key, "startDate"]}
          noStyle
        >
          <Input type='date' />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "END_DATE", def: "End Date" }),
      dataIndex: "endDate",
      key: "endDate",
      render: (text: string, record: any) => (
        <Form.Item
          name={["data", record.key, "endDate"]}
          noStyle
        >
          <Input type='date' />
        </Form.Item>
      ),
    },
  ];

  const handleSubmit = async (values: any) => {
    // Make API call here
    try {
      const { data } = values;
      const requestPayload = {
        data: Object.keys(data).map((key) => ({
          name: key,
          startDate: data[key].startDate || "",
          endDate: data[key].endDate || "",
        })),
      };
      const response = await post(addEditTimeline, requestPayload);
      const { dataResponse } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <FaCheck />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const getTimelineData = async () => {
    try {
      const response = await post(getTimeline, {});
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        form.setFieldsValue({
          data: data.reduce((acc: any, item: any) => {
            acc[item.name] = {
              startDate: item.startDate,
              endDate: item.endDate,
            };
            return acc;
          }, {}),
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getTimelineData();
  }, []);

  return (
    <div>
      <section className={styles.formHeader}>
        <Typography.Title
          level={3}
          style={{ margin: "0", color: "var(--bs-primary)" }}
        >
          {Text({ tid: "EDIT_TIMELINE", def: "Edit Timeline" })}
        </Typography.Title>

        <Button
          key='reject'
          onClick={() => onCloseDrawer()}
        >
          {Text({ tid: "CLOSE", def: "Close" })}
        </Button>
      </section>
      <div className={styles.formWrapper}>
        <Form
          layout='vertical'
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item>
            <Table
              dataSource={stages}
              columns={columns}
              pagination={false}
              rowKey={(record) => `${record.stage}-${record.status}`}
            />
          </Form.Item>
          <Form.Item className={styles.formFooter}>
            <Button
              type='primary'
              htmlType='submit'
              style={{ float: "right" }}
            >
              {Text({ tid: "SUBMIT", def: "Submit" })}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default TimelineForm;
